import DOMPurify from 'dompurify';
import { remark } from 'remark';
import remarkHtml from 'remark-html';

import { remarkEmbedCodes } from './remark-embed-codes';
import { remarkStripImages } from './remark-strip-images';

const pipeline = remark()
  .use(remarkEmbedCodes)
  .use(remarkHtml, { sanitize: false });

const previewPipeline = remark()
  .use(remarkEmbedCodes)
  .use(remarkStripImages)
  .use(remarkHtml, { sanitize: false });

/**
 * Transforms raw message content into safe, rich HTML ready for rendering.
 *
 * Processing steps:
 * 1. Resolve custom embed code expressions (e.g. date embeds) via a remark plugin
 * 2. Convert markdown syntax (including `![alt](url)` images) to HTML via remark-html
 * 3. Sanitize the resulting HTML with DOMPurify to prevent XSS
 */
export function transformMessageContent(raw: string): string {
  const html = String(pipeline.processSync(raw));
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['certificate', 'playerid'],
  });
}

/** Matches <br> and <br/> tags. */
const BR_TAG = /<br\s*\/?>/gi;

/** Matches opening/closing tags for block-level elements that create line breaks. */
const BLOCK_BOUNDARY_TAG =
  /<\/?(p|div|li|tr|th|td|h[1-6]|blockquote|section|article|header|footer)[^>]*>/gi;

/**
 * Converts an HTML string to plain text by:
 * 1. Replacing <br> and block-boundary tags with spaces (so words don't run together)
 * 2. Stripping all remaining tags via DOMPurify
 */
function htmlToPlainText(html: string): string {
  const fragment = DOMPurify.sanitize(
    html.replace(BR_TAG, ' ').replace(BLOCK_BOUNDARY_TAG, ' '),
    { ALLOWED_TAGS: [], ALLOWED_ATTR: [], RETURN_DOM_FRAGMENT: true },
  );
  return fragment.textContent;
}

/**
 * Transforms raw message content into a plain-text preview suitable for
 * compact list displays. All HTML tags, images, and newlines are stripped so
 * the result is a single line of unstyled text.
 *
 * Processing steps:
 * 1. Resolve custom embed code expressions (dates, etc.) via a remark plugin
 * 2. Strip all markdown images from the AST
 * 3. Convert to HTML via remark-html
 * 4. Replace line-breaking tags with spaces, then strip all remaining HTML tags
 * 5. Collapse all whitespace / newlines to a single space
 */
export function transformMessagePreview(raw: string): string {
  const html = String(previewPipeline.processSync(raw));
  return htmlToPlainText(html).replace(/\s+/g, ' ').trim();
}
